import { useMemo } from 'react';
import {
  Box,
  Flex,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useColorModeValue,
  List,
  ListItem,
} from '@chakra-ui/react';
import { useTable, useSortBy } from 'react-table';
import { format } from 'date-fns';
import { getBankName } from '../../../users/data/banks';

const TestResultsNotRegisteredUsersTable = ({ notRegistered }) => {
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

  const columns = useMemo(
    () => [
      {
        Header: 'טלפון נבדק',
        accessor: 'phoneNumber',
      },
      {
        Header: 'טלפון בכספת',
        accessor: (row) => row.userDto?.phoneNumber || '',
      },
      {
        Header: 'תז נבדקת',
        accessor: 'govId',
      },
      {
        Header: 'תז בכספת',
        accessor: (row) => row.userDto?.details?.govId || '',
      },
      {
        Header: 'מעסיק',
        accessor: 'employerId',
        width: '60px',
        Cell: ({ value }) => (
          <Text textAlign="center" fontSize="sm">
            {value || ''}
          </Text>
        ),
      },
      {
        Header: 'סניף',
        accessor: (row) => row.userDto?.details?.branch || '',
      },
      {
        Header: 'בנק',
        accessor: (row) => getBankName(row.userDto?.details?.bankAccountDetails?.bankId || ''),
      },
      {
        Header: 'מספר חשבון',
        accessor: (row) => row.userDto?.details?.bankAccountDetails?.accountId || '',
      },
      {
        Header: 'שם מלא',
        accessor: (row) =>
          row.userDto ? `${row.userDto.firstName || ''} ${row.userDto.lastName || ''}`.trim() : '',
      },
      {
        Header: 'שכר שעתי',
        accessor: 'hourlyGrossRate',
        width: '100px',
        Cell: ({ value, row }) => {
          // Try to get hourly salary from multiple sources
          const hourlyRate = value ||
                           row.original.userDto?.details?.salaryDetails?.hourlyGrossRate ||
                           null;

          if (hourlyRate && hourlyRate > 0) {
            return (
              <Text fontSize="sm" textAlign="center">
                ₪{hourlyRate.toFixed(2)}
              </Text>
            );
          }

          return (
            <Text fontSize="sm" textAlign="center" color="gray.500">
              לא זמין
            </Text>
          );
        },
      },
      {
        Header: 'משמרות',
        accessor: 'integrationData',
        width: '200px',
        Cell: ({ value }) => {
          if (!value) return null;

          if (!value.success) {
            return (
              <Text fontSize="sm" color="red.500">
                האינטגרציה נכשלה
              </Text>
            );
          }

          if (value.success && value.timeReporting?.length > 0) {
            return (
              <List spacing={1} fontSize="sm">
                {value.timeReporting.map((report, idx) => (
                  <ListItem key={idx}>
                    {format(new Date(report.shiftDate), 'dd/MM/yyyy')} -{' '}
                    {Math.round(report.workingHours * 10) / 10} שעות
                  </ListItem>
                ))}
              </List>
            );
          }

          return <Text fontSize="sm">אין משמרות</Text>;
        },
      },
      {
        Header: 'שגיאות',
        accessor: 'errors',
        width: '180px',
        Cell: ({ value }) => {
          if (!value || value.length === 0) {
            return null;
          }

          return (
            <List spacing={1} fontSize="sm">
              {value.map((error, idx) => (
                <ListItem key={idx} color="red.500">
                  {error}
                </ListItem>
              ))}
            </List>
          );
        },
      },
    ],
    [],
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
    {
      columns,
      data: notRegistered || [],
    },
    useSortBy,
  );

  return (
    <Box>
      <Text fontWeight="bold" fontSize="lg" mb={3}>
        משתמשים לא רשומים
      </Text>
      <Box
        maxH="400px"
        overflowX="auto"
        overflowY="auto"
        border="1px"
        borderColor={borderColor}
        borderRadius="md"
      >
        {notRegistered?.length > 0 ? (
          <Table {...getTableProps()} variant="simple" size="sm">
            <Thead position="sticky" top={0} bg="white" zIndex="1">
              {headerGroups.map((headerGroup, idx) => (
                <Tr {...headerGroup.getHeaderGroupProps()} key={idx}>
                  {headerGroup.headers.map((column, idx) => (
                    <Th
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                      key={idx}
                      borderColor={borderColor}
                      textAlign="right"
                      whiteSpace="nowrap"
                    >
                      <Flex
                        justify="space-between"
                        align="center"
                        fontSize={{ sm: '10px', lg: '12px' }}
                        color="gray.400"
                      >
                        {column.render('Header')}
                      </Flex>
                    </Th>
                  ))}
                </Tr>
              ))}
            </Thead>
            <Tbody {...getTableBodyProps()}>
              {!!rows?.length &&
                rows.map((row) => {
                  prepareRow(row);
                  return (
                    <Tr {...row.getRowProps()} key={row.id || row.index}>
                      {row.cells.map((cell, idx) => (
                        <Td
                          {...cell.getCellProps()}
                          key={idx}
                          fontSize={{ sm: '14px' }}
                          color={textColor}
                          borderColor={borderColor}
                          minW={cell.column.width || '150px'}
                          maxW={cell.column.width}
                          width={cell.column.width}
                        >
                          {cell.render('Cell')}
                        </Td>
                      ))}
                    </Tr>
                  );
                })}
            </Tbody>
          </Table>
        ) : (
          <Text p={4} textAlign="center">
            אין משתמשים לא רשומים להצגה
          </Text>
        )}
      </Box>
    </Box>
  );
};

export default TestResultsNotRegisteredUsersTable;
